"use client";
import { B as ComboboxList, F as ComboboxEmpty, H as ComboboxInput, I as ComboboxItem, L as ComboboxPopup, R as ComboboxPositioner, U as ComboboxRoot, V as ComboboxTrigger, z as ComboboxPortal } from "../chunks/vendor-base-ui-n0ony04mhklkmnfd.js";
import { t as useImpressionRef } from "../chunks/use-impression-ref-k081rlv0dt8gci3d.js";
import { r as useAccessibleNameWarning, t as hasAccessibleName } from "../chunks/accessible-name-h2kn8kxv0zjyg24j.js";
import { t as cn } from "../chunks/cn-dk9nmc4mhy1765ud.js";
import { n as usePortalContainer } from "../chunks/portal-provider-mv0x5fh2psxbmcl8.js";
import { Box } from "./box.js";
import { ATOMS_INPUT_VARIANTS, inputVariants } from "./input.js";
import { createContext, forwardRef, useCallback, useContext } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import IconChevronDown from "@tabler/icons-react/dist/esm/icons/IconChevronDown.mjs";
import IconChevronUp from "@tabler/icons-react/dist/esm/icons/IconChevronUp.mjs";
//#region src/components/combobox/combobox.tsx
/** Combobox variant definitions. */
var ATOMS_COMBOBOX_VARIANTS = { size: {
	xs: ATOMS_INPUT_VARIANTS.size.xs,
	sm: ATOMS_INPUT_VARIANTS.size.sm,
	md: ATOMS_INPUT_VARIANTS.size.base,
	lg: ATOMS_INPUT_VARIANTS.size.lg
} };
var ATOMS_COMBOBOX_DEFAULT_VARIANTS = { size: "md" };
var ComboboxSizeContext = createContext("md");
var popupStyles = {
	xs: {
		radius: "rounded-xl",
		padding: "p-1",
		maxHeight: "max-h-44",
		sideOffset: 4
	},
	sm: {
		radius: "rounded-2xl",
		padding: "p-1",
		maxHeight: "max-h-60",
		sideOffset: 6
	},
	md: {
		radius: "rounded-3xl",
		padding: "p-1.5",
		maxHeight: "max-h-72",
		sideOffset: 8
	},
	lg: {
		radius: "rounded-3xl",
		padding: "p-2",
		maxHeight: "max-h-80",
		sideOffset: 10
	}
};
var itemStyles = {
	xs: {
		padding: "px-2.5 py-2.5",
		radius: "rounded-lg",
		typography: "text-body-md"
	},
	sm: {
		padding: "p-4",
		radius: "rounded-xl",
		typography: "text-body-md"
	},
	md: {
		padding: "p-5",
		radius: "rounded-2xl",
		typography: "text-body-lg"
	},
	lg: {
		padding: "px-6 py-6",
		radius: "rounded-2xl",
		typography: "text-body-xl"
	}
};
var INPUT_SIZE = {
	xs: "xs",
	sm: "sm",
	md: "base",
	lg: "lg"
};
var chevronRightOffset = {
	xs: "right-2.5",
	sm: "right-3",
	md: "right-4",
	lg: "right-5"
};
var StyledInput = forwardRef(function StyledInput({ className, error = false, ...props }, ref) {
	const size = useContext(ComboboxSizeContext);
	return /* @__PURE__ */ jsx(ComboboxInput, {
		...props,
		ref,
		className: cn(inputVariants({
			size: INPUT_SIZE[size],
			error
		}), "atoms-combobox-input group w-full disabled:cursor-not-allowed", !error && "not-disabled:data-filled:ring-od-border-selected", error ? "data-popup-open:ring-od-border-error data-popup-open:ring-2" : "data-popup-open:ring-od-border-accent-brand data-popup-open:ring-2", className)
	});
});
function StyledTrigger({ className, ...props }) {
	return /* @__PURE__ */ jsx(ComboboxTrigger, {
		...props,
		className: cn("group m-0 cursor-pointer bg-transparent p-0 outline-none", "data-disabled:cursor-not-allowed data-disabled:opacity-50", className)
	});
}
function StyledPopup({ className, ...props }) {
	const styles = popupStyles[useContext(ComboboxSizeContext)];
	return /* @__PURE__ */ jsx(ComboboxPopup, {
		...props,
		className: cn("flex flex-col", "max-w-(--available-width) min-w-(--anchor-width)", styles.maxHeight, styles.padding, styles.radius, "bg-od-bg-primary text-od-content-primary", "ring-2 ring-od-border-focused-accent-brand", className)
	});
}
function StyledItem({ className, ...props }) {
	const styles = itemStyles[useContext(ComboboxSizeContext)];
	return /* @__PURE__ */ jsx(ComboboxItem, {
		...props,
		className: cn("group cursor-pointer", styles.padding, styles.radius, styles.typography, "data-highlighted:bg-od-bg-hover-primary", "data-selected:bg-od-bg-hover-primary", "active:bg-od-bg-hover-tertiary", className)
	});
}
function StyledEmpty({ className, children, ...props }) {
	const styles = itemStyles[useContext(ComboboxSizeContext)];
	return /* @__PURE__ */ jsx(ComboboxEmpty, {
		...props,
		className: cn("shrink-0 text-od-content-inactive-secondary empty:m-0 empty:p-0", styles.padding, styles.typography, className),
		children
	});
}
var areOptionsEqual = (a, b) => a.value === b.value;
/**
* Searchable single-select dropdown. Pass `options`, wire `value` /
* `onValueChange`, and you're done — DX parity with the bricks-next
* `SearchableDropdown`.
*
* Filter behaviour: case-insensitive substring match on `label`. When the
* query has no matches the popup shows "No items found."
*
* @example
* ```tsx
* <Combobox
*   options={[{ value: 'apple', label: 'Apple' }]}
*   value={value}
*   onValueChange={setValue}
*   placeholder="Pick a fruit"
* />
* ```
*/
var Combobox = forwardRef(function Combobox({ size = "md", options, value, defaultValue, onValueChange, placeholder, disabled, className, name, error, id, analyticsName, trackImpression, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, "aria-describedby": ariaDescribedBy, "aria-invalid": ariaInvalid }, ref) {
	const portalContainer = usePortalContainer();
	useAccessibleNameWarning("Combobox", hasAccessibleName({
		"aria-label": ariaLabel,
		"aria-labelledby": ariaLabelledBy,
		id
	}));
	const viewRef = useImpressionRef(Boolean(analyticsName && trackImpression), analyticsName, "inputViewEvent", ref);
	const handleValueChange = useCallback((v) => onValueChange?.(v), [onValueChange]);
	return /* @__PURE__ */ jsx(ComboboxSizeContext.Provider, {
		value: size,
		children: /* @__PURE__ */ jsxs(ComboboxRoot, {
			id,
			items: options,
			value,
			defaultValue,
			onValueChange: handleValueChange,
			disabled,
			name,
			isItemEqualToValue: areOptionsEqual,
			children: [/* @__PURE__ */ jsxs(Box, {
				className: cn("relative inline-block w-full", className),
				children: [/* @__PURE__ */ jsx(StyledInput, {
					ref: viewRef,
					placeholder,
					"aria-label": ariaLabel,
					"aria-labelledby": ariaLabelledBy,
					"aria-describedby": ariaDescribedBy,
					"aria-invalid": ariaInvalid,
					error,
					className: "pr-12"
				}), /* @__PURE__ */ jsxs(StyledTrigger, {
					"aria-label": "Show options",
					className: cn("absolute top-1/2 flex -translate-y-1/2 items-center justify-center", chevronRightOffset[size]),
					children: [/* @__PURE__ */ jsx(IconChevronDown, {
						size: 16,
						stroke: 3,
						className: "text-od-content-primary group-data-popup-open:hidden"
					}), /* @__PURE__ */ jsx(IconChevronUp, {
						size: 16,
						stroke: 3,
						className: "hidden text-od-content-primary group-data-popup-open:block"
					})]
				})]
			}), /* @__PURE__ */ jsx(ComboboxPortal, {
				container: portalContainer ?? void 0,
				children: /* @__PURE__ */ jsx(ComboboxPositioner, {
					align: "start",
					sideOffset: popupStyles[size].sideOffset,
					children: /* @__PURE__ */ jsxs(StyledPopup, { children: [/* @__PURE__ */ jsx(StyledEmpty, { children: "No items found." }), /* @__PURE__ */ jsx(ComboboxList, {
						className: cn("min-h-0 flex-1 overflow-y-auto overscroll-contain scroll-pt-2 scroll-pb-2", itemStyles[size].radius),
						children: (item) => /* @__PURE__ */ jsx(StyledItem, {
							value: item,
							children: item.label
						}, item.value)
					})] })
				})
			})]
		})
	});
});
Combobox.displayName = "Combobox";
//#endregion
export { ATOMS_COMBOBOX_DEFAULT_VARIANTS, ATOMS_COMBOBOX_VARIANTS, Combobox };

